import * as React from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface TableEmptyRowProps {
  colSpan: number;
  message: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  action?: React.ReactNode;
}

/** Consistent empty state rendered inside a table body (keeps the header visible). */
export function TableEmptyRow({ colSpan, message, icon: Icon, action }: TableEmptyRowProps) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="h-32 text-center">
        <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
          {Icon && <Icon className="h-8 w-8 opacity-40" />}
          <p className="text-sm">{message}</p>
          {action}
        </div>
      </TableCell>
    </TableRow>
  );
}

interface TableLoadingRowsProps {
  colSpan: number;
  rows?: number;
}

/** Consistent skeleton rows rendered inside a table body while loading. */
export function TableLoadingRows({ colSpan, rows = 5 }: TableLoadingRowsProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i} className="hover:bg-transparent">
          <TableCell colSpan={colSpan} className="py-3">
            <Skeleton className="h-6 w-full" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

interface EmptyStateProps {
  message: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  action?: React.ReactNode;
  className?: string;
}

/** Consistent empty state for non-table lists (card grids, etc.). */
export function EmptyState({ message, icon: Icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground',
        className
      )}
    >
      {Icon && <Icon className="h-10 w-10 opacity-40" />}
      <p className="text-sm">{message}</p>
      {action}
    </div>
  );
}
